import { getApiInst } from '~/atoms/MangoApi'

import {
  createReportingApi,
  type PaginatedQueuesResponse,
  type QueueAgentsResponse,
  type QueueCallsResponse,
  type QueueMetricsApiResponse,
  type ReportingApiClient,
} from '@mangovoice/reporting-ui'
import type {
  AnswerRateResponse,
  ExtensionComparisonRequest,
  ExtensionComparisonResponse,
  NumberComparisonResponse,
  PhoneNumber,
} from '@mangovoice/types'

function buildReportingParams(request: {
  start: string
  end: string
  pbx: string
  phone_number?: string | string[]
  tz?: string
  group_by?: string
}): string {
  const params = new URLSearchParams()
  params.set('start', request.start)
  params.set('end', request.end)
  params.set('pbx', request.pbx)

  const phoneNumber = request.phone_number
    ? Array.isArray(request.phone_number)
      ? request.phone_number.join(',')
      : request.phone_number
    : undefined
  if (phoneNumber) params.set('phone_number', phoneNumber)
  if (request.tz) params.set('tz', request.tz)
  if (request.group_by) params.set('group_by', request.group_by)

  return params.toString()
}

async function apiCall<T>(
  call: () => Promise<{
    ok: boolean
    data?: unknown
    status: number
  }>,
): Promise<{
  data: T | null
  ok: boolean
  status?: number
  problem?: string | null
}> {
  const response = await call()
  return {
    data: response.ok ? (response.data as T) : null,
    ok: response.ok,
    status: response.status,
  }
}

const reportingApiClient: ReportingApiClient = {
  getAnswerRate: async (request, signal) => {
    const qs = buildReportingParams(request)
    return apiCall<AnswerRateResponse>(() =>
      getApiInst()
        .getHttpTransport()
        .get(`/app/v1/reporting/answer-rate/?${qs}`, undefined, { signal }),
    )
  },

  getNumberComparison: async (request, signal) => {
    const qs = buildReportingParams(request)
    return apiCall<NumberComparisonResponse>(() =>
      getApiInst()
        .getHttpTransport()
        .get(`/app/v1/reporting/number-comparison/?${qs}`, undefined, {
          signal,
        }),
    )
  },

  getExtensionComparison: async (
    request: ExtensionComparisonRequest,
    signal,
  ) => {
    const params = new URLSearchParams()
    params.set('start', request.start)
    params.set('end', request.end)
    params.set('pbx', request.pbx)
    if (request.direction) params.set('direction', request.direction)
    if (request.extension?.length) {
      params.set('extension', request.extension.join(','))
    }
    return apiCall<ExtensionComparisonResponse>(() =>
      getApiInst()
        .getHttpTransport()
        .get(
          `/app/v1/reporting/extension-comparison/?${params.toString()}`,
          undefined,
          { signal },
        ),
    )
  },

  getExtensionLines: async (params: { pbx_id: number }) => {
    const response = await apiCall<{
      results: { number: string; name: string }[]
    }>(() =>
      getApiInst()
        .getHttpTransport()
        .get(`/app/v1/extensions/?pbx_id=${params.pbx_id}&limit=1000`),
    )
    return {
      ...response,
      data:
        response.data?.results?.map((e) => ({
          number: e.number,
          name: e.name,
        })) ?? [],
    }
  },

  getPhoneNumbers: async (params) => {
    const qs = new URLSearchParams({
      pbx_id: String(params.pbx_id),
      limit: String(params.limit ?? 1000),
      ...(params.offset ? { offset: String(params.offset) } : {}),
    }).toString()
    return apiCall<{ results: PhoneNumber[]; count: number }>(() =>
      getApiInst().getHttpTransport().get(`/app/v1/phone-numbers/?${qs}`),
    )
  },

  getSystemSettings: async (params) => {
    return apiCall<{ default_time_zone?: string }>(() =>
      getApiInst()
        .getHttpTransport()
        .get(`/app/v1/pbx/${params.pbx_id}/system-settings/`),
    )
  },

  getQueueDashboardQueues: async (pbxId, signal) =>
    apiCall<PaginatedQueuesResponse>(() =>
      getApiInst()
        .getHttpTransport()
        .get(
          `/app/v1/extensions/?pbx_id=${pbxId}&type=ExtensionCallQueue`,
          undefined,
          { signal },
        ),
    ),

  getQueueDashboardCalls: async (pbxUuid, queueNumber, signal) =>
    apiCall<QueueCallsResponse>(() =>
      getApiInst()
        .getHttpTransport()
        .get(
          `/app/v1/call-center-queues/members/?pbx_uuid=${pbxUuid}&number=${queueNumber}`,
          undefined,
          { signal },
        ),
    ),

  getQueueDashboardAgents: async (pbxUuid, queueNumber, signal) =>
    apiCall<QueueAgentsResponse>(() =>
      getApiInst()
        .getHttpTransport()
        .get(
          `/app/v1/call-center-queues/agents/?pbx_uuid=${pbxUuid}&number=${queueNumber}`,
          undefined,
          { signal },
        ),
    ),

  getQueueMetrics: async (params, signal) => {
    const qs = new URLSearchParams({
      start: params.start,
      end: params.end,
      pbxes: params.pbxes,
      queue: params.queue,
    })
    return apiCall<QueueMetricsApiResponse>(() =>
      getApiInst()
        .getHttpTransport()
        .get(`/app/v1/reporting/queue/summary/?${qs.toString()}`, undefined, {
          signal,
        }),
    )
  },
}

export const reportingApi = createReportingApi(reportingApiClient)
